import { useCallback, useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { useData } from "@/contexts/DataContext";
import { areAdsSuppressed, maybeShowInterstitial, pushAdSuppression, subscribeAdSuppression } from "@/lib/ads";

/**
 * Whether ads should be shown to the current user.
 *
 * Ads appear ONLY on native platforms for non-subscribers:
 * - Web (dailyhisaab.com) never shows ads — AdMob is native-only.
 * - Active pro/ultimate subscribers (`isPaid`) are ad-free. This also covers
 *   users who redeemed reward points for temporary Premium time, because that
 *   grants an active `pro` plan (see `upgradeToProWithPoints`).
 */
export function useShowAds(): boolean {
  const { isPaid } = useData();
  return Capacitor.isNativePlatform() && !isPaid;
}

/** Reactive read of the global ad-suppression state (see `pushAdSuppression`). */
export function useAdsSuppressed(): boolean {
  const [suppressed, setSuppressed] = useState(areAdsSuppressed());
  useEffect(() => subscribeAdSuppression(() => setSuppressed(areAdsSuppressed())), []);
  return suppressed;
}

/**
 * Hide the ad banner while `active` is true (e.g. cart open, success popup).
 * Mount this in any billing/checkout surface; it composes via reference count.
 */
export function useSuppressAdsWhile(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    return pushAdSuppression();
  }, [active]);
}

/**
 * Returns a stable function to attempt a (capped) interstitial at a natural
 * break — call it AFTER a completed action (report download, product save).
 * No-ops for paying users and on web.
 */
export function useAdTrigger(): () => void {
  const showAds = useShowAds();
  return useCallback(() => {
    if (showAds) void maybeShowInterstitial();
  }, [showAds]);
}
